import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardService, notificationService, locationService, ticketsService } from '@/services';
import { apiClient } from '@/services/api-client';
import { useThemeStore, useAuthStore } from '@/stores';
import { usePermission } from '@/hooks/usePermission';
import {
  RefreshCw,
  Plus,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  HelpCircle,
  ChevronRight,
  ChevronLeft,
  X,
  Send,
  Bell,
  BellRing,
  Volume2,
  VolumeX,
  Trash2,
  ShieldCheck,
  ShieldAlert,
  Lock,
  Key,
} from 'lucide-react';
import { playTicketChime, showDesktopNotification, requestDesktopNotificationPermission } from '@/utils/ticket-notifications';


/* ─────────────────────────────────────── Types ─────────────────────────────────────── */
interface SupportTicket {
  id: string;
  displayId?: string;
  subject: string;
  category: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL';
  status: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
  customerName?: string;
  customerId?: string | null;
  summary?: string;
  zone?: string | null;
  callerPhone?: string | null;
  creatorName?: string | null;
  issue?: string | null;
}

interface NewTicketToast {
  id: string;
  tickets: SupportTicket[];
  timestamp: number;
}

/* ─────────────────────────────────────── Fallback data ─────────────────────────────── */
const fallbackTickets: SupportTicket[] = [];


/* ─────────────────────────────────────── Label maps ────────────────────────────────── */
const priorityLabels: Record<string, { label: string; color: string; bg: string }> = {
  LOW: { label: 'منخفضة', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  NORMAL: { label: 'عادية', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  HIGH: { label: 'عالية', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  CRITICAL: { label: 'حرجة', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

const statusLabels: Record<string, { label: string; color: string; bg: string }> = {
  // Main categorized ones
  OPEN: { label: 'مفتوحة', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  IN_PROGRESS: { label: 'قيد المعالجة', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  RESOLVED: { label: 'تم الحل', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  CLOSED: { label: 'مغلقة', color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
  
  // Specific Portal Statuses
  Canceled: { label: 'ملغاة (Canceled)', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
  Resolved: { label: 'تم الحل (Resolved)', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  'In progress': { label: 'قيد المعالجة (In progress)', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
  New: { label: 'جديدة (New)', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
  'Contractor feedback': { label: 'ملاحظات المقاول (Contractor feedback)', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  Reopened: { label: 'أعيد فتحها (Reopened)', color: '#ec4899', bg: 'rgba(236,72,153,0.1)' },
  'On hold': { label: 'معلقة (On hold)', color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)' },
  close: { label: 'مغلقة (close)', color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
  Closed: { label: 'مغلقة (Closed)', color: '#f87171', bg: 'rgba(239,68,68,0.1)' },
  'Waiting for respons': { label: 'بانتظار الرد (Waiting for respons)', color: '#e11d48', bg: 'rgba(225,29,72,0.1)' },
  'Waiting for response': { label: 'بانتظار الرد (Waiting for response)', color: '#e11d48', bg: 'rgba(225,29,72,0.1)' },
};

export const mapStatusToMain = (status: string): 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED' => {
  const s = String(status || '').trim();
  const lower = s.toLowerCase();
  
  // 1. OPEN (مفتوحة / جديدة / معاد فتحها / بانتظار الرد)
  if (
    lower === 'new' || 
    lower === 'open' || 
    lower === 'reopened' || 
    lower === 'waiting for respons' || 
    lower === 'waiting for response' || 
    lower === 'waiting_for_respons' || 
    lower === 'waiting_for_response' ||
    lower === 'مفتوحة' ||
    lower === 'جديدة' ||
    lower === 'بانتظار الرد' ||
    lower === 'أعيد فتحها'
  ) {
    return 'OPEN';
  }

  // 2. IN_PROGRESS (قيد المعالجة / ملاحظات المقاول / معلقة)
  if (
    lower === 'in progress' || 
    lower === 'in_progress' || 
    lower === 'inprogress' || 
    lower === 'contractor feedback' || 
    lower === 'contractor_feedback' || 
    lower === 'on hold' || 
    lower === 'on_hold' || 
    lower === 'قيد المعالجة' ||
    lower === 'ملاحظات المقاول' ||
    lower === 'معلقة'
  ) {
    return 'IN_PROGRESS';
  }

  // 3. RESOLVED (تم الحل / محلولة)
  if (
    lower === 'resolved' || 
    lower === 'محلولة' || 
    lower === 'تم الحل'
  ) {
    return 'RESOLVED';
  }

  // 4. CLOSED (مغلقة / ملغاة)
  if (
    lower === 'close' || 
    lower === 'closed' || 
    lower === 'canceled' || 
    lower === 'مغلقة' || 
    lower === 'ملغاة'
  ) {
    return 'CLOSED';
  }
  
  // Default fallbacks for mapped main keys
  if (s === 'OPEN') return 'OPEN';
  if (s === 'IN_PROGRESS') return 'IN_PROGRESS';
  if (s === 'RESOLVED') return 'RESOLVED';
  if (s === 'CLOSED') return 'CLOSED';
  
  return 'OPEN';
};

/* ─────────────────────────────── Audio Notification (Web Audio API) ─────────────────── */
let sharedAudioContext: AudioContext | null = null;

function getAudioContext() {
  if (!sharedAudioContext) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      sharedAudioContext = new AudioContextClass();
    }
  }
  return sharedAudioContext;
}

function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch((e) => console.warn('Failed to resume AudioContext:', e));
    }

    // Create a pleasant tri-tone chime
    const playTone = (freq: number, startTime: number, duration: number, volume: number) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startTime);

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    playTone(523.25, now, 0.4, 0.4);        // C5
    playTone(659.25, now + 0.15, 0.4, 0.4);  // E5
    playTone(783.99, now + 0.30, 0.6, 0.5);  // G5
  } catch (e) {
    console.warn('Audio notification failed:', e);
  }
}

const determinePriority = (subject?: string): 'LOW' | 'NORMAL' | 'HIGH' | 'CRITICAL' => {
  if (!subject) return 'NORMAL';
  const sub = subject.toLowerCase();
  
  const highKeywords = [
    "توقف كامل",
    "انقطاع كابل",
    "بأنتظار الرد",
    "waiting for response",
    "قطع",
    "مشكلة عامة",
    "توقف",
    "خلل",
    "طوارئ",
    "emergency",
    "down",
    "outage"
  ];
  
  const lowKeywords = [
    "استفسار",
    "ترقية باقة",
    "معلومات",
    "سؤال",
    "info",
    "question",
    "inquiry",
    "upgrade"
  ];
  
  for (const kw of highKeywords) {
    if (sub.includes(kw)) {
      if (kw === "توقف كامل" || kw === "طوارئ" || kw === "emergency" || kw === "down" || kw === "outage") {
        return 'CRITICAL';
      }
      return 'HIGH';
    }
  }
  
  for (const kw of lowKeywords) {
    if (sub.includes(kw)) {
      return 'LOW';
    }
  }
  
  return 'NORMAL';
};

/* ─────────────────────────────── Portal Date Formatter ────────────────────────── */
const formatPortalDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${mins}`;
  } catch (e) {
    return dateStr;
  }
};

/* ─────────────────────────────── Map raw API ticket ────────────────────────────────── */
function mapTicket(t: any): SupportTicket {
  if (t.customerName && t.subject && t.zone !== undefined && t.creatorName !== undefined) return t as SupportTicket;
  
  const statusStr = t.status?.displayValue || t.status?.id || t.status || t.state?.displayValue || t.state?.id || t.state || 'New';
  const rawStatus = typeof statusStr === 'string' ? statusStr : 'New';
  const subject = t.self?.displayValue || t.summary || t.subject || 'رفع تذكرة من تطبيق الوطني';
  const priority = t.priority && t.priority !== 'NORMAL' ? t.priority : determinePriority(subject);

  return {
    id: t.self?.id || t.id,
    displayId: t.displayId || t.id,
    subject,
    category: t.category?.displayValue || t.category?.name || (typeof t.category === 'string' ? t.category : null) || 'Service request - Contact Center',
    priority,
    status: rawStatus,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt || t.createdAt,
    closedAt: t.closedAt || null,
    customerName: t.customer?.displayValue || t.customerName || t.customer || '—',
    customerId: t.customer?.id || t.customerId || null,
    summary: t.summary || t.details || '',
    zone: t.zone?.displayValue || t.zone?.id || (typeof t.zone === 'string' ? t.zone : null) || '—',
    callerPhone: t.caller?.phoneNumber || t.callerPhone || '—',
    creatorName: t.user?.displayValue || t.creator?.displayValue || t.partner?.displayValue || t.customer?.displayValue || '—',
    issue: t.issue?.displayValue || t.issue || '—'
  };
}

/* ══════════════════════════════ New Ticket Toast Component ══════════════════════════════ */
interface ToastProps {
  toast: NewTicketToast;
  onDismiss: () => void;
}

const NewTicketToastAlert: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const count = toast.tickets.length;
  const firstTicket = toast.tickets[0];

  const email = localStorage.getItem('notification_email') || '';
  const emailEnabled = localStorage.getItem('notification_email_enabled') !== 'false';
  const hasEmailNotification = email && emailEnabled;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '28px',
        left: '28px',
        zIndex: 9999,
        width: '380px',
        maxWidth: 'calc(100vw - 56px)',
        animation: 'toastSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        direction: 'rtl',
      }}
    >
      <style>{`
        @keyframes toastSlideUp {
          from { transform: translateY(100px) scale(0.9); opacity: 0; }
          to   { transform: translateY(0)     scale(1);   opacity: 1; }
        }
        @keyframes bellRing {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(15deg); }
          30% { transform: rotate(-13deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(5deg); }
        }
        @keyframes pulseRing {
          0%   { transform: scale(1);   opacity: 0.6; }
          70%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes gradientShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      {/* Glow effect behind the card */}
      <div
        style={{
          position: 'absolute',
          inset: '-2px',
          borderRadius: '18px',
          background: 'linear-gradient(135deg, #ef4444, #f59e0b, #3b82f6)',
          backgroundSize: '200% 200%',
          animation: 'gradientShift 3s ease infinite',
          filter: 'blur(8px)',
          opacity: 0.5,
          zIndex: -1,
        }}
      />

      {/* Main card */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0f1c35 0%, #0a1020 100%)',
          border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: '16px',
          padding: '18px 20px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(239,68,68,0.2)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background shimmer */}
        <div
          style={{
            position: 'absolute',
            top: '-40px',
            right: '-40px',
            width: '160px',
            height: '160px',
            background: 'radial-gradient(circle, rgba(239,68,68,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          {/* Close btn */}
          <button
            onClick={onDismiss}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '5px',
              color: '#94a3b8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.15)';
              e.currentTarget.style.color = '#ef4444';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            <X size={14} />
          </button>

          {/* Title + bell icon */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '14px' }}>
                {count > 1 ? `${count} تذاكر جديدة وردت!` : 'تذكرة دعم جديدة!'}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '1px' }}>
                {new Date(toast.timestamp).toLocaleTimeString('ar-IQ', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>

            {/* Animated bell with pulse ring */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: 'rgba(239,68,68,0.4)',
                  animation: 'pulseRing 1.5s ease-out infinite',
                }}
              />
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(239,68,68,0.3), rgba(245,158,11,0.2))',
                  border: '1px solid rgba(239,68,68,0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <BellRing
                  size={22}
                  color="#ef4444"
                  style={{ animation: 'bellRing 0.8s ease-in-out 2' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Ticket details */}
        <div
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: '10px',
            padding: '12px 14px',
            marginBottom: count > 1 ? '10px' : '0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: '10.5px',
                color: priorityLabels[firstTicket?.priority]?.color || '#ef4444',
                background: priorityLabels[firstTicket?.priority]?.bg || 'rgba(239,68,68,0.12)',
                padding: '2px 6px',
                borderRadius: '4px',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {priorityLabels[firstTicket?.priority]?.label || 'حرجة'}
            </span>
            <div style={{ textAlign: 'right', flex: 1 }}>
              <div
                style={{
                  color: '#e2e8f0',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '260px',
                }}
                title={firstTicket?.subject}
              >
                {firstTicket?.subject || 'تذكرة دعم فني جديدة'}
              </div>
              <div style={{ color: '#64748b', fontSize: '11px', marginTop: '3px' }}>
                {firstTicket?.customerName || '—'}
                {firstTicket?.displayId && (
                  <span style={{ marginRight: '6px', color: '#475569', fontFamily: 'monospace' }}>
                    #{firstTicket.displayId}
                  </span>
                )}
              </div>
            </div>
          </div>
        {hasEmailNotification && (
          <div
            style={{
              marginTop: '10px',
              padding: '6px 10px',
              background: 'rgba(52,211,153,0.1)',
              border: '1px solid rgba(52,211,153,0.2)',
              borderRadius: '8px',
              color: '#34d399',
              fontSize: '11px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'flex-end',
            }}
          >
            <span>تم إرسال تنبيه تلقائي إلى: <strong>{email}</strong></span>
            <Send size={11} />
          </div>
        )}
        </div>

        {count > 1 && (
          <div style={{ color: '#64748b', fontSize: '11.5px', textAlign: 'right', marginTop: '6px' }}>
            + {count - 1} تذاكر أخرى جديدة — راجع القائمة أدناه
          </div>
        )}
      </div>
    </div>
  );
};

const getTimeDiff = (t1?: string | number | null, t2?: string | number | null): string => {
  if (!t1 || !t2) return '—';
  try {
    const d1 = typeof t1 === 'number' ? t1 : new Date(t1).getTime();
    const d2 = typeof t2 === 'number' ? t2 : new Date(t2).getTime();
    if (isNaN(d1) || isNaN(d2)) return '—';
    const diffMs = Math.abs(d2 - d1);
    const secs = Math.floor(diffMs / 1000);
    if (secs < 60) return `${secs} ثانية`;
    const mins = Math.floor(secs / 60);
    const remSecs = secs % 60;
    if (mins < 60) {
      return remSecs > 0 ? `${mins} دقيقة و ${remSecs} ثانية` : `${mins} دقيقة`;
    }
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return remMins > 0 ? `${hours} ساعة و ${remMins} دقيقة` : `${hours} ساعة`;
  } catch {
    return '—';
  }
};

/* ══════════════════════════════════ Main Page Component ══════════════════════════════════ */
const TicketsPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';
  // Use username + sessionId as a unique scope key — different accounts AND different sessions
  // NEVER share React Query cache. sessionId changes on every new login even for the same user.
  const currentUserId = useAuthStore((s) => s.user?.username || s.user?.id || 'anonymous');
  const sessionId = useAuthStore((s) => s.sessionId || 'no-session');
  const currentSessionKey = `${currentUserId}:${sessionId}`;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Permission Guard Hook (Canonical Effective Permissions) ──
  const { permissions } = usePermission();

  // ── UI State ──
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── Notification State ──
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('notification_sound_enabled') !== 'false');
  const [activeToast, setActiveToast] = useState<NewTicketToast | null>(null);
  const [newTicketCount, setNewTicketCount] = useState(0);
  const [isBellRinging, setIsBellRinging] = useState(false);

  // ── Notification Logs State & Query ──
  const [showLogPanel, setShowLogPanel] = useState(false);
  const [logsPageNumber, setLogsPageNumber] = useState(1);
  const {
    data: userLogs,
    isLoading: isLogsLoading,
    isFetching: isLogsFetching,
    isError: isLogsError,
    refetch: refetchUserLogs,
  } = useQuery({
    // Include currentSessionKey (username + sessionId) to prevent cross-account cache bleed
    queryKey: ['userLogs', currentSessionKey],
    queryFn: async () => {
      const response = await notificationService.getUserLogs({ type: 'TICKET' });
      const rawLogs = response.data || response || [];
      return (Array.isArray(rawLogs) ? rawLogs : []).filter((log: any) => log.type === 'TICKET') as any[];
    },
    enabled: true,
    staleTime: 10000,
    refetchInterval: showLogPanel ? 15000 : false,
    refetchIntervalInBackground: false,
    placeholderData: (prevData: any) => prevData,
  });

  // Calculate unread count from backend logs
  const unreadLogsCount = React.useMemo(() => {
    if (!userLogs || !Array.isArray(userLogs)) return 0;
    return userLogs.filter((log: any) => log.status === 'UNREAD').length;
  }, [userLogs]);

  // Combined total badge count for Bell icon
  const totalUnreadBadge = newTicketCount + unreadLogsCount;

  const logsPageSize = 10;
  const paginatedUserLogs = React.useMemo(() => {
    if (!userLogs) return [];
    // Sort logs by date descending so the newest are shown first!
    const sorted = [...userLogs].sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
    const start = (logsPageNumber - 1) * logsPageSize;
    return sorted.slice(start, start + logsPageSize);
  }, [userLogs, logsPageNumber]);

  const totalLogsCount = userLogs?.length || 0;
  const totalLogsPages = Math.ceil(totalLogsCount / logsPageSize) || 1;

  // ── Refs for tracking known tickets & instant notifications ──
  const knownTicketIdsRef = useRef<Set<string>>(new Set());
  const isFirstLoadRef = useRef(true);
  const pageLoadedAtRef = useRef<number>(Date.now());

  // Web Audio API unlock for mobile browsers
  useEffect(() => {
    const unlockAudio = () => {
      try {
        const ctx = getAudioContext();
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().then(() => {
            console.log('Shared AudioContext unlocked successfully via user gesture');
          }).catch(err => {
            console.warn('Failed to resume shared AudioContext on user gesture:', err);
          });
        }
      } catch (e) {
        console.warn('Audio unlock failed:', e);
      }
      
      // Remove listeners after first interaction
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };

    window.addEventListener('click', unlockAudio);
    window.addEventListener('touchstart', unlockAudio);
    window.addEventListener('keydown', unlockAudio);

    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  // ── Reset all page state & refs when switching user accounts or sessions ──
  useEffect(() => {
    knownTicketIdsRef.current = new Set();
    isFirstLoadRef.current = true;
    setNewTicketCount(0);
    setActiveToast(null);
    setSelectedTicketId(null);
    setSearchTerm('');
    setStatusFilter('ALL');
    setPageNumber(1);
    queryClient.invalidateQueries({ queryKey: ['supportTicketsList'] });
    queryClient.invalidateQueries({ queryKey: ['supportTicketsSummary'] });
    queryClient.invalidateQueries({ queryKey: ['userLogs'] });
  }, [currentSessionKey, queryClient]);

  /* ─── Ticket Details Query ─── */
  const { data: ticketDetails, isLoading: isDetailsLoading, refetch: refetchDetails } = useQuery({
    queryKey: ['supportTicketDetails', currentSessionKey, selectedTicketId],
    queryFn: async () => {
      if (!selectedTicketId) return null;
      try {
        const response = await dashboardService.supportTicketDetails(selectedTicketId);
        const resObj = response?.data || response;
        const t = resObj?.model || resObj?.data || resObj?.item || resObj || {};

        const displayId = t.displayId || t.self?.displayValue || selectedTicketId;
        const subject = t.self?.displayValue || t.subject || t.summary || t.details || 'رفع تذكرة من تطبيق الوطني';
        const category = t.category?.displayValue || t.category?.name || (typeof t.category === 'string' ? t.category : null) || 'Service request - Contact Center';
        const rawStatus = (t.status || t.state?.displayValue || 'Open').toString();
        const status = rawStatus.toUpperCase() === 'INPROGRESS' || rawStatus.toUpperCase() === 'IN_PROGRESS'
          ? 'IN_PROGRESS'
          : rawStatus.toUpperCase() === 'CLOSED' ? 'CLOSED' : rawStatus.toUpperCase();

        const priority = t.priority && t.priority !== 'NORMAL' ? t.priority : determinePriority(subject);

        const zone = t.zone?.displayValue || t.zone?.id || (typeof t.zone === 'string' ? t.zone : null) || '—';
        const callerPhone = t.caller?.phoneNumber || t.callerPhone || '—';
        const callerType = t.caller?.type?.displayValue || t.callerType || '—';
        const creatorName = t.user?.displayValue || t.creator?.displayValue || t.partner?.displayValue || t.customer?.displayValue || '—';
        const customerName = t.customer?.displayValue || t.customerName || '—';
        const customerId = t.customer?.id || t.customerId || null;
        const summary = t.details || t.summary || t.description || '';

        return {
          id: t.self?.id || t.id || selectedTicketId,
          displayId,
          subject,
          category,
          priority,
          status,
          rawStatus,
          createdAt: t.createdAt,
          updatedAt: t.updatedAt,
          closedAt: t.closedAt,
          zone,
          callerPhone,
          callerType,
          creatorName,
          customerName,
          customerId,
          summary,
          issue: t.issue?.displayValue || t.issue || '—',
        };
      } catch (err) {
        console.error('Failed to load full ticket details:', err);
        return null;
      }
    },
    enabled: !!selectedTicketId,
    staleTime: 0,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  /* ─── Ticket Comments Query (Endpoint #3) ─── */
  const {
    data: ticketComments,
    isLoading: isCommentsLoading,
    refetch: refetchComments,
  } = useQuery({
    queryKey: ['supportTicketComments', currentSessionKey, selectedTicketId],
    queryFn: async () => {
      if (!selectedTicketId) return [];
      const response = await dashboardService.supportTicketComments(selectedTicketId, { pageSize: 10, pageNumber: 1 });
      const resp = response as any;
      return resp.data ?? resp.items ?? resp;
    },
    enabled: !!selectedTicketId,
    staleTime: 0,
    refetchInterval: 15000,
    refetchIntervalInBackground: true,
  });

  /* ─── Ticket Statuses Query (Endpoint #4) ─── */
  const { data: ticketStatuses } = useQuery({
    queryKey: ['supportTicketsStatuses', currentSessionKey],
    queryFn: async () => {
      const response = await dashboardService.supportTicketsStatuses();
      return response.data || response || [];
    },
    staleTime: 60000,
  });

  /* ─── Ticket Categories Query (Endpoint #5) ─── */
  const { data: ticketCategories } = useQuery({
    queryKey: ['supportTicketsCategories', currentSessionKey],
    queryFn: async () => {
      const response = await dashboardService.supportTicketsCategories();
      return response.data || response || [];
    },
    staleTime: 60000,
  });

  /* ─── Locations / Zones Query (Endpoint #6) ─── */
  const { data: locationZones } = useQuery({
    queryKey: ['locationZones', currentSessionKey],
    queryFn: async () => {
      const response = await locationService.zones();
      return response.data || response || [];
    },
    staleTime: 60000,
  });

  /* ─── Refresh control ref ─── */
  const forceRefreshRef = React.useRef(false);

  /* ─── Tickets Summary Query ─── */
  const {
    data: ticketsSummary,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['supportTicketsSummary', currentSessionKey],
    queryFn: async () => {
      const response = await dashboardService.supportTickets({
        forceRefresh: forceRefreshRef.current
      });
      return response.data;
    },
    staleTime: 45000,
    refetchInterval: 60000,
    refetchIntervalInBackground: false,
    placeholderData: (prevData: any, prevQuery: any) => {
      if (prevQuery?.queryKey?.[1] === currentSessionKey) {
        return prevData;
      }
      return undefined;
    },
  });

/* ─── Map status name to ID for server-side filtering ─── */
const mapStatusToId = (status: string): string | undefined => {
  // Check live fetched status objects first if available
  if (Array.isArray(ticketStatuses) && ticketStatuses.length > 0) {
    const matched = ticketStatuses.find((st: any) => {
      const name = (st.displayValue || st.name || st.code || st.id || '').toString().toLowerCase();
      return name === status.toLowerCase() || name.replace(/\s+/g, '_') === status.toLowerCase();
    });
    if (matched?.id) return matched.id;
  }

  const s = status.toLowerCase().replace(/_/g, ' ').trim();
  if (s === 'canceled') return '6e5f4218-f46b-1410-fe9a-0050ba5d6c38';
  if (s === 'resolved') return 'ae7f411e-f46b-1410-009b-0050ba5d6c38';
  if (s === 'in progress') return '7e9f1204-f46b-1410-fb9a-0050ba5d6c38';
  if (s === 'new' || s === 'open') return 'ae5f2f10-f46b-1410-fd9a-0050ba5d6c38';
  if (s === 'contractor feedback') return 'e59b4ef2-928b-45c2-adf1-b6c1731bf3ba';
  if (s === 'reopened') return 'f063ebbe-fdc6-4982-8431-d8cfa52fedcf';
  if (s === 'on hold') return 'b83fbafd-a4a5-402b-aff4-ca653dee0cb6';
  if (s === 'close' || s === 'closed') return '3e7f420c-f46b-1410-fc9a-0050ba5d6c38';
  if (s === 'waiting for response' || s === 'waiting for respons') return '3859c6e7-cbcb-486b-ba53-77808fe6e593';
  return undefined;
};

  /* ─── Tickets List Query (Endpoint #1) ─── */
  const {
    data: rawTicketsResponse,
    isLoading: isListLoading,
    isFetching: isListFetching,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['supportTicketsList', currentSessionKey, pageNumber, pageSize, statusFilter, searchTerm],
    queryFn: async () => {
      const isMainGroup = ['OPEN', 'IN_PROGRESS', 'CLOSED'].includes(statusFilter.toUpperCase());
      const params: any = {
        pageSize: isMainGroup ? Math.max(pageSize * 5, 50) : (pageSize || 12),
        pageNumber: isMainGroup ? 1 : (pageNumber || 1),
        'sortCriteria.property': 'createdAt',
        'sortCriteria.direction': 'desc',
        hierarchyLevel: 0,
        additionalFilter: 'ForMe', // Matching Endpoint #1 requirement exactly
      };

      // Only pass statusId if it's a specific single-status filter (e.g. 'Resolved', 'New', etc.)
      if (statusFilter && statusFilter !== 'ALL' && !isMainGroup) {
        const mappedId = mapStatusToId(statusFilter);
        if (mappedId) {
          params.statusId = mappedId;
        }
      }

      if (searchTerm.trim()) {
        const term = searchTerm.trim();
        const isDisplayId = /^(cc-|sre-|\d)/i.test(term) || term.toLowerCase().startsWith('cc') || term.toLowerCase().startsWith('sre');
        if (isDisplayId) {
          params.displayId = term;
        } else {
          params.customerName = term;
        }
      }

      if (forceRefreshRef.current) {
        params.forceRefresh = true;
      }

      const response = await dashboardService.supportTicketsList(params);
      return response.data;
    },
    staleTime: 12000,
    refetchInterval: 15000, // Poll every 15 seconds to reduce flicker
    refetchIntervalInBackground: true,
    placeholderData: (prevData: any, prevQuery: any) => {
      if (prevQuery?.queryKey?.[1] === currentSessionKey) {
        return prevData;
      }
      return undefined;
    },
  });

  /* ─── Comment submit ─── */
  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTicketId) return;
    setIsSubmittingComment(true);
    try {
      await dashboardService.createTicketComment(selectedTicketId, newComment.trim());
      setNewComment('');
      refetchComments();
    } catch (error) {
      console.error('Failed to submit comment:', error);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  /* ─── Refresh handler ─── */
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const isRefreshing = isManualRefreshing || isSummaryFetching || isListFetching;
  const handleRefresh = async () => {
    if (isManualRefreshing) return;
    setIsManualRefreshing(true);
    forceRefreshRef.current = true;
    try {
      const promises: Promise<any>[] = [
        refetchSummary({ cancelRefetch: true }),
        refetchList({ cancelRefetch: true }),
      ];
      if (selectedTicketId) {
        promises.push(refetchDetails({ cancelRefetch: true }));
        promises.push(refetchComments({ cancelRefetch: true }));
      }
      if (showLogPanel) {
        promises.push(refetchUserLogs());
      }
      await Promise.allSettled(promises);
    } catch (err) {
      console.error('Manual ticket refresh error:', err);
    } finally {
      forceRefreshRef.current = false;
      setIsManualRefreshing(false);
    }
  };

  /* ─── Map raw response to ticket array ─── */
  const ticketsData: SupportTicket[] = React.useMemo(() => {
    if (rawTicketsResponse) {
      const resp = rawTicketsResponse as any;
      const items = resp.items ?? resp.data ?? resp;
      if (Array.isArray(items)) {
        return items.map(mapTicket);
      }
    }
    return [];
  }, [rawTicketsResponse]);

  // Selected ticket fallback from list (placed after ticketsData initialization)
  const selectedTicketFromList = React.useMemo(() => {
    if (!selectedTicketId) return null;
    return ticketsData.find((t) => t.id === selectedTicketId || t.displayId === selectedTicketId) || null;
  }, [selectedTicketId, ticketsData]);

  // Combined Active Ticket Data (Instant rendering + Portal live details)
  const activeTicket = ticketDetails || (selectedTicketFromList ? {
    id: selectedTicketFromList.id,
    displayId: selectedTicketFromList.displayId || selectedTicketFromList.id,
    subject: selectedTicketFromList.subject || 'رفع تذكرة من تطبيق الوطني',
    category: selectedTicketFromList.category || 'Service request - Contact Center',
    priority: selectedTicketFromList.priority || 'NORMAL',
    status: selectedTicketFromList.status || 'OPEN',
    rawStatus: selectedTicketFromList.status || 'Open',
    createdAt: selectedTicketFromList.createdAt,
    updatedAt: selectedTicketFromList.updatedAt || selectedTicketFromList.createdAt,
    closedAt: null,
    zone: (selectedTicketFromList as any).zone?.displayValue || (selectedTicketFromList as any).zone || '—',
    callerPhone: '—',
    callerType: 'Customer',
    creatorName: selectedTicketFromList.customerName || '—',
    customerName: selectedTicketFromList.customerName || '—',
    customerId: selectedTicketFromList.customerId || null,
    summary: selectedTicketFromList.summary || '',
    issue: '—'
  } : null);

  /* ─── New ticket detection & notification ─── */
  useEffect(() => {
    if (ticketsData.length === 0) return;

    const incomingIds = ticketsData.map((t) => t.id).filter(Boolean);

    // If on page 1 with no search filter, check for incoming tickets
    if (pageNumber === 1 && (!statusFilter || statusFilter === 'ALL') && !searchTerm.trim()) {
      if (isFirstLoadRef.current) {
        // First load: seed known IDs
        incomingIds.forEach((id) => knownTicketIdsRef.current.add(id));
        isFirstLoadRef.current = false;
        return;
      }

      // Find genuinely new tickets that appeared on page 1
      const newTickets = ticketsData.filter((t) => !knownTicketIdsRef.current.has(t.id));

      // Register all incoming IDs so we don't re-trigger for them
      incomingIds.forEach((id) => knownTicketIdsRef.current.add(id));

      if (newTickets.length > 0) {
        // Update badge count
        setNewTicketCount((prev) => prev + newTickets.length);
        setIsBellRinging(true);
        setTimeout(() => setIsBellRinging(false), 1600);

        // Refresh notification logs if panel is open
        if (showLogPanel) {
          refetchUserLogs();
        }

        // 1. Dispatch immediate background mobile push via Backend
        try {
          apiClient.post('/notifications/trigger-ticket-push', {
            tickets: newTickets.map((t) => ({
              id: t.id,
              displayId: t.displayId,
              customerName: t.customerName,
              subject: t.subject,
              priority: t.priority,
              status: t.status,
              createdAt: t.createdAt
            })),
            userId: currentUserId || 'city'
          }).catch((pushErr) => {
            console.warn('[TicketsPage] Mobile push trigger error:', pushErr);
          });
        } catch (dispatchErr) {
          console.warn('[TicketsPage] Failed to trigger mobile push:', dispatchErr);
        }

        // 2. Play audible chime
        if (soundEnabled) {
          playTicketChime();
        }

        // 3. Show native desktop notification
        const first = newTickets[0];
        const title = newTickets.length === 1 
          ? `تذكرة جديدة: #${first.displayId || first.id}` 
          : `وصلت ${newTickets.length} تذاكر جديدة!`;
        const body = `${first.customerName} — ${first.subject}`;
        showDesktopNotification(title, { body });

        // 4. Show in-app toast
        setActiveToast({
          id: `toast-${Date.now()}`,
          tickets: newTickets,
          timestamp: Date.now(),
        });

        // Auto-dismiss toast after 8 seconds
        setTimeout(() => setActiveToast(null), 8000);
      }
    } else {
      // In sub-pages or filtered views, just remember the IDs silently
      incomingIds.forEach((id) => knownTicketIdsRef.current.add(id));
    }
  }, [ticketsData, soundEnabled, pageNumber, statusFilter, searchTerm]);

  /* ─── Derived display data ─── */
  const displayTickets = ticketsData;

  const totalTicketsCount = React.useMemo(() => {
    if (rawTicketsResponse) {
      const resp = rawTicketsResponse as any;
      return resp.totalCount ?? resp.total ?? (resp.items ?? resp.data ?? resp).length ?? 0;
    }
    return 0;
  }, [rawTicketsResponse]);

  const { summary, summaryState } = React.useMemo(() => {
    if (isSummaryLoading && !ticketsSummary) {
      return {
        summary: { total: null, open: null, inProgress: null, resolved: null, closed: null },
        summaryState: 'loading' as const,
      };
    }

    if (isSummaryError || !ticketsSummary || typeof ticketsSummary !== 'object') {
      return {
        summary: { total: null, open: null, inProgress: null, resolved: null, closed: null },
        summaryState: 'error' as const,
      };
    }

    const ticketsSummaryObj = ticketsSummary as Record<string, any>;
    const getSummaryValue = (key: string): number | null => {
      if (typeof ticketsSummaryObj[key] === 'number' && !isNaN(ticketsSummaryObj[key])) {
        return ticketsSummaryObj[key];
      }
      if (ticketsSummaryObj[key] !== undefined && ticketsSummaryObj[key] !== null) {
        const val = Number(ticketsSummaryObj[key]);
        return isNaN(val) ? null : val;
      }
      if (ticketsSummaryObj.model) {
        if (typeof ticketsSummaryObj.model[key] === 'number' && !isNaN(ticketsSummaryObj.model[key])) {
          return ticketsSummaryObj.model[key];
        }
        if (ticketsSummaryObj.model[key] !== undefined && ticketsSummaryObj.model[key] !== null) {
          const val = Number(ticketsSummaryObj.model[key]);
          return isNaN(val) ? null : val;
        }
      }
      const snakeKey = key.replace(/([A-Z])/g, "_$1").toLowerCase();
      if (typeof ticketsSummaryObj[snakeKey] === 'number' && !isNaN(ticketsSummaryObj[snakeKey])) {
        return ticketsSummaryObj[snakeKey];
      }
      if (ticketsSummaryObj[snakeKey] !== undefined && ticketsSummaryObj[snakeKey] !== null) {
        const val = Number(ticketsSummaryObj[snakeKey]);
        return isNaN(val) ? null : val;
      }
      if (ticketsSummaryObj.model) {
        if (typeof ticketsSummaryObj.model[snakeKey] === 'number' && !isNaN(ticketsSummaryObj.model[snakeKey])) {
          return ticketsSummaryObj.model[snakeKey];
        }
        if (ticketsSummaryObj.model[snakeKey] !== undefined && ticketsSummaryObj.model[snakeKey] !== null) {
          const val = Number(ticketsSummaryObj.model[snakeKey]);
          return isNaN(val) ? null : val;
        }
      }
      return null;
    };

    const openVal = getSummaryValue('open') ?? getSummaryValue('openCount');
    const inProgressVal = getSummaryValue('inProgress') ?? getSummaryValue('in_progress') ?? getSummaryValue('inProgressCount');
    const resolvedVal = getSummaryValue('resolved') ?? getSummaryValue('resolvedCount');
    const closedVal = getSummaryValue('closed') ?? getSummaryValue('closedCount');
    const totalVal = getSummaryValue('total') ?? getSummaryValue('totalCount') ?? getSummaryValue('count');

    // Strict contract validation: all 5 metrics must be valid numbers (even if 0)
    const isValid = (
      typeof totalVal === 'number' && !isNaN(totalVal) &&
      typeof openVal === 'number' && !isNaN(openVal) &&
      typeof inProgressVal === 'number' && !isNaN(inProgressVal) &&
      typeof resolvedVal === 'number' && !isNaN(resolvedVal) &&
      typeof closedVal === 'number' && !isNaN(closedVal)
    );

    if (!isValid) {
      return {
        summary: { total: null, open: null, inProgress: null, resolved: null, closed: null },
        summaryState: 'error' as const,
      };
    }

    return {
      summary: {
        total: totalVal,
        open: openVal,
        inProgress: inProgressVal,
        resolved: resolvedVal,
        closed: closedVal,
      },
      summaryState: 'success' as const,
    };
  }, [ticketsSummary, isSummaryLoading, isSummaryError]);

  /* ─── Status Matching Helper ─── */
  const matchesStatus = React.useCallback((ticketStatus: string, filter: string) => {
    if (!filter || filter === 'ALL') return true;

    const tStatus = String(ticketStatus || '').trim();
    const lowerT = tStatus.toLowerCase();
    const lowerF = filter.toLowerCase();

    // 1. Check if filter is a main card category: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'
    if (['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].includes(filter.toUpperCase())) {
      return mapStatusToMain(tStatus) === filter.toUpperCase();
    }

    // 2. Direct exact or normalized name match
    if (lowerT === lowerF) return true;
    if (lowerT.replace(/\s+/g, '_') === lowerF.replace(/\s+/g, '_')) return true;

    // 3. Normalized specific portal matching
    if (lowerF.includes('contractor') && lowerT.includes('contractor')) return true;
    if (lowerF.includes('waiting') && lowerT.includes('waiting')) return true;
    if (lowerF.includes('reopen') && lowerT.includes('reopen')) return true;
    if (lowerF.includes('hold') && lowerT.includes('hold')) return true;
    if (lowerF.includes('close') && lowerT.includes('close')) return true;
    if (lowerF.includes('cancel') && lowerT.includes('cancel')) return true;
    if (lowerF.includes('resolv') && lowerT.includes('resolv')) return true;
    if (lowerF.includes('new') && lowerT.includes('new')) return true;

    return false;
  }, []);

  /* ─── Filtered and Paginated Tickets ─── */
  const filteredTickets = React.useMemo(() => {
    return displayTickets.filter((ticket: any) => {
      const matchesSearch =
        !searchTerm.trim() ||
        (ticket.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.displayId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ticket.customerName && ticket.customerName.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStat = matchesStatus(ticket.status || ticket.rawStatus || '', statusFilter);
      const matchesPri = priorityFilter === 'ALL' || (ticket.priority || '').toUpperCase() === priorityFilter.toUpperCase();

      return matchesSearch && matchesStat && matchesPri;
    });
  }, [displayTickets, searchTerm, statusFilter, priorityFilter, matchesStatus]);

  const totalFilteredCount = filteredTickets.length;
  const totalPages = Math.max(Math.ceil(totalFilteredCount / pageSize), 1);
  const paginatedTickets = React.useMemo(() => {
    const start = (pageNumber - 1) * pageSize;
    return filteredTickets.slice(start, start + pageSize);
  }, [filteredTickets, pageNumber, pageSize]);

  /* ══════════════════ RENDER ══════════════════ */
  return (
    <div style={{ padding: '20px', direction: 'rtl', minHeight: '100%' }}>

      {/* ─── Global Keyframes ─── */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(239,68,68,0.6); }
          50% { transform: scale(1.15); box-shadow: 0 0 0 6px rgba(239,68,68,0); }
        }
        @keyframes toastSlideUp {
          from { transform: translateY(100px) scale(0.9); opacity: 0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes bellRing {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(15deg); }
          30% { transform: rotate(-13deg); }
          45% { transform: rotate(10deg); }
          60% { transform: rotate(-8deg); }
          75% { transform: rotate(5deg); }
        }
        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.6; }
          70% { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (max-width: 768px) {
          .tickets-page-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          .tickets-header-title-container {
            width: 100% !important;
            text-align: right !important;
          }
          .tickets-title-row {
            justify-content: flex-start !important;
          }
          .tickets-header-buttons-container {
            width: 100% !important;
            justify-content: flex-start !important;
            flex-wrap: wrap !important;
            gap: 8px !important;
          }
          
          /* ─── Ticket Table Card Layout Overrides ─── */
          .ticket-table {
            display: block !important;
            width: 100% !important;
            border: none !important;
            background: transparent !important;
          }
          .ticket-table thead {
            display: none !important;
          }
          .ticket-table tbody {
            display: block !important;
            width: 100% !important;
          }
          .ticket-table .ticket-card-row {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            padding: 16px !important;
            margin-bottom: 16px !important;
            gap: 12px 0 !important;
            background: rgba(13, 31, 60, 0.6) !important;
            border: 1px solid rgba(255, 255, 255, 0.06) !important;
            border-radius: 16px !important;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1) !important;
          }
          body.light-mode .ticket-table .ticket-card-row {
            background: #ffffff !important;
            border: 1px solid #cbd5e1 !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02) !important;
          }
          
          .ticket-table td {
            display: block !important;
            padding: 0 !important;
            border-bottom: none !important;
            background: transparent !important;
            width: 100% !important;
            text-align: right !important;
          }
          .ticket-table td::before {
            display: none !important;
          }
          
          /* 1. Ticket ID */
          .ticket-table .td-id {
            width: 60% !important;
            order: 1 !important;
            text-align: right !important;
            font-size: 13.5px !important;
            font-weight: 700 !important;
            color: #3b82f6 !important;
          }
          body.light-mode .ticket-table .td-id {
            color: #2563eb !important;
          }
          .ticket-table .td-id::before {
            content: "رقم التذكرة: " !important;
            display: inline !important;
            font-size: 12px !important;
            font-weight: 600;
            color: #64748b !important;
          }
          body.light-mode .ticket-table .td-id::before {
            color: #475569 !important;
          }
          
          /* 2. Last Update Date */
          .ticket-table .td-update {
            width: 40% !important;
            order: 2 !important;
            text-align: left !important;
            font-size: 12.5px !important;
            color: #64748b !important;
            font-weight: 500 !important;
          }
          body.light-mode .ticket-table .td-update {
            color: #475569 !important;
          }
          
          .ticket-table .td-id, .ticket-table .td-update {
            border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
            padding-bottom: 10px !important;
          }
          body.light-mode .ticket-table .td-id, body.light-mode .ticket-table .td-update {
            border-bottom: 1px solid #f1f5f9 !important;
          }
          
          /* 3. Customer */
          .ticket-table .td-customer {
            width: 100% !important;
            order: 3 !important;
            text-align: right !important;
            margin-top: 4px !important;
          }
          .ticket-table .td-customer::before {
            content: "المشترك:" !important;
            display: block !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #64748b !important;
            margin-bottom: 4px !important;
          }
          body.light-mode .ticket-table .td-customer::before {
            color: #475569 !important;
          }
          .ticket-table .td-customer > div {
            align-items: flex-start !important;
          }
          
          /* 4. Subject and Details block */
          .ticket-table .td-subject {
            width: 100% !important;
            order: 4 !important;
            background: rgba(10, 22, 40, 0.4) !important;
            border-radius: 12px !important;
            padding: 12px !important;
            margin-top: 6px !important;
            border: 1px solid rgba(255, 255, 255, 0.03) !important;
          }
          body.light-mode .ticket-table .td-subject {
            background: #f8fafc !important;
            border: 1px solid #e2e8f0 !important;
          }
          .ticket-table .td-subject::before {
            content: "الموضوع / التفاصيل:" !important;
            display: block !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            color: #64748b !important;
            margin-bottom: 6px !important;
          }
          body.light-mode .ticket-table .td-subject::before {
            color: #475569 !important;
          }
          .ticket-table .td-subject > div {
            align-items: flex-start !important;
            text-align: right !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .ticket-table .td-subject span {
            text-align: right !important;
            white-space: normal !important;
          }
          
          /* 5. Priority */
          .ticket-table .td-priority {
            width: 50% !important;
            order: 5 !important;
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
            margin-top: 8px !important;
          }
          .ticket-table .td-priority::before {
            content: "الأولوية:" !important;
            display: inline !important;
            font-size: 11.5px !important;
            font-weight: 700 !important;
            color: #64748b !important;
          }
          body.light-mode .ticket-table .td-priority::before {
            color: #475569 !important;
          }
          
          /* 6. Status */
          .ticket-table .td-status {
            width: 50% !important;
            order: 6 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            gap: 6px !important;
            margin-top: 8px !important;
          }
          .ticket-table .td-status::before {
            content: "الحالة:" !important;
            display: inline !important;
            font-size: 11.5px !important;
            font-weight: 700 !important;
            color: #64748b !important;
          }
          body.light-mode .ticket-table .td-status::before {
            color: #475569 !important;
          }
          
          /* 7. Action Row */
          .ticket-table .td-action {
            width: 100% !important;
            order: 7 !important;
            display: flex !important;
            justify-content: flex-end !important;
            margin-top: 12px !important;
            border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
            padding-top: 12px !important;
          }
          body.light-mode .ticket-table .td-action {
            border-top: 1px solid #f1f5f9 !important;
          }
        }
        
        /* ─── Pagination Buttons Styling ─── */
        .pagination-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          border-radius: 10px;
          padding: 8px 16px;
          font-size: 13.5px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1.5px solid transparent;
          outline: none;
        }
        
        /* Light mode active buttons */
        body.light-mode .pagination-btn {
          background: #ffffff;
          border-color: #cbd5e1;
          color: #1e293b;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        body.light-mode .pagination-btn:hover:not(:disabled) {
          background: #f8fafc;
          border-color: #2563eb;
          color: #2563eb;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08);
          transform: translateY(-1px);
        }
        body.light-mode .pagination-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        
        /* Dark mode active buttons */
        .pagination-btn {
          background: rgba(255, 255, 255, 0.06);
          border-color: rgba(255, 255, 255, 0.1);
          color: #e2e8f0;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
        }
        .pagination-btn:hover:not(:disabled) {
          background: rgba(37, 99, 235, 0.15);
          border-color: rgba(59, 130, 246, 0.4);
          color: #60a5fa;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
          transform: translateY(-1px);
        }
        .pagination-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        
        /* Disabled states */
        .pagination-btn:disabled {
          cursor: not-allowed !important;
          box-shadow: none !important;
          transform: none !important;
        }
        body.light-mode .pagination-btn:disabled {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
          color: #94a3b8 !important;
          opacity: 0.5;
        }
        .pagination-btn:disabled {
          background: rgba(255, 255, 255, 0.02) !important;
          border-color: rgba(255, 255, 255, 0.04) !important;
          color: #475569 !important;
        }
      `}</style>

      {/* ─── Toast Notification ─── */}
      {activeToast && (
        <NewTicketToastAlert
          toast={activeToast}
          onDismiss={() => setActiveToast(null)}
        />
      )}

      {/* ─── Page Header ─── */}
      <div
        className="tickets-page-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}
      >
        <div className="tickets-header-title-container" style={{ textAlign: 'right' }}>
          <div className="tickets-title-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end', marginBottom: '4px' }}>
            <h1 style={{ color: '#e2e8f0', fontSize: '22px', fontWeight: 700, margin: 0 }}>
              {t('tickets.title') || 'تذاكر الدعم الفني'}
            </h1>
            {/* Live polling indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span
                style={{
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  background: '#22c55e',
                  display: 'inline-block',
                  animation: 'badgePulse 2s ease infinite',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: '#22c55e', fontSize: '11px', fontWeight: 600 }}>مباشر</span>
            </div>
          </div>
          <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
            متابعة المشاكل الفنية وبلاغات الأعطال
          </p>
        </div>

        <div className="tickets-header-buttons-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>


          {/* Sound toggle button */}
          <button
            onClick={() => {
              setSoundEnabled((v) => {
                const nextVal = !v;
                localStorage.setItem('notification_sound_enabled', String(nextVal));
                return nextVal;
              });
            }}
            title={soundEnabled ? 'إيقاف صوت التنبيه' : 'تفعيل صوت التنبيه'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: soundEnabled ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.04)',
              border: soundEnabled ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '8px',
              color: soundEnabled ? '#60a5fa' : '#475569',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>

          {/* Notification bell with badge */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={async () => {
                const nextState = !showLogPanel;
                setShowLogPanel(nextState);
                setNewTicketCount(0);

                if (nextState) {
                  // Instantly update local React Query cache to clear the badge visually
                  queryClient.setQueryData(['userLogs', currentSessionKey], (oldData: any) => {
                    if (!oldData || !Array.isArray(oldData)) return [];
                    return oldData.map((log: any) => ({ ...log, status: 'READ' }));
                  });

                  try {
                    await notificationService.markRead({ all: true, type: 'TICKET' });
                    await refetchUserLogs();
                  } catch (err) {
                    console.error('Failed to mark notifications as read:', err);
                  }
                }
              }}

              title="عرض سجل التنبيهات"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: totalUnreadBadge > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.04)',
                border: totalUnreadBadge > 0 ? '1px solid rgba(239,68,68,0.3)' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                padding: '8px',
                color: totalUnreadBadge > 0 ? '#f87171' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <Bell size={16} style={{ animation: isBellRinging ? 'bellRing 0.8s ease-in-out' : 'none' }} />
            </button>
            {totalUnreadBadge > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '-5px',
                  left: '-5px',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  color: '#fff',
                  fontSize: '10px',
          fontWeight: 800,
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #060e1b',
                  animation: 'badgePulse 1.5s ease infinite',
                  lineHeight: 1,
                }}
              >
                {totalUnreadBadge > 99 ? '99+' : totalUnreadBadge}
              </span>
            )}
          </div>

          {/* Log Panel toggle button */}
          <button
            onClick={() => setShowLogPanel(p => !p)}
            title="سجل التنبيهات واختبار الخلفية"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: showLogPanel ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.04)',
              border: showLogPanel ? '1px solid rgba(59,130,246,0.3)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: showLogPanel ? '#60a5fa' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '13px',
              transition: 'all 0.2s',
            }}
          >
            <BellRing size={14} style={{ marginLeft: '6px' }} />
            {showLogPanel ? 'إخفاء سجل التنبيهات' : 'سجل التنبيهات'}
          </button>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            title={isRefreshing ? 'جاري تحديث التذاكر من الخادم...' : 'تحديث التذاكر والإحصائيات'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: '8px',
              padding: '8px 14px',
              color: isLight ? '#475569' : '#94a3b8',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              opacity: isRefreshing ? 0.7 : 1,
              fontSize: '13px',
              transition: 'all 0.2s',
            }}
          >
            <RefreshCw size={14} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : {}} />
            {isRefreshing ? 'جاري التحديث...' : (t('common.refresh') || 'تحديث')}
          </button>

          <button
            disabled={!permissions['tickets.create']}
            title={!permissions['tickets.create'] ? '🔒 غير مصرح (يتطلب صلاحية tickets.create)' : 'إضافة تذكرة جديدة'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: !permissions['tickets.create'] ? 'rgba(100,116,139,0.3)' : 'linear-gradient(90deg, #3b82f6, #6366f1)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 14px',
              color: !permissions['tickets.create'] ? '#94a3b8' : '#fff',
              cursor: !permissions['tickets.create'] ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              opacity: !permissions['tickets.create'] ? 0.6 : 1,
            }}
          >
            <Plus size={14} />
            <span>{!permissions['tickets.create'] ? '🔒 تذكرة جديدة (مقيد)' : (t('tickets.addTicket') || 'تذكرة جديدة')}</span>
          </button>
        </div>
      </div>



      {/* PERMISSION CHECK: Access Denied State */}
      {!permissions['tickets.read'] ? (
        <div
          style={{
            background: 'rgba(239, 68, 68, 0.05)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '16px',
            padding: '60px 20px',
            textAlign: 'center',
            direction: 'rtl',
            marginTop: '20px',
          }}
        >
          <ShieldAlert size={56} color="#ef4444" style={{ margin: '0 auto 16px', display: 'block' }} />
          <h2 style={{ color: '#f87171', fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>
            عذراً! غير مصرح لك باستعراض تذاكر الدعم الفني
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px', lineHeight: 1.6 }}>
            تم تطبيق نظام الصلاحيات على هذه الصفحة، وحسابك لا يمتلك الصلاحية التالية:
            <code style={{ background: 'rgba(239,68,68,0.2)', color: '#fca5a5', padding: '2px 8px', borderRadius: '4px', margin: '0 6px' }}>
              tickets.read
            </code>
          </p>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '12px' }}>
            <AlertCircle size={14} />
            <span>يمكنك مراجعة مسؤول النظام لتعديل صلاحياتك في قسم Users & Roles.</span>
          </div>
        </div>
      ) : (
        <>

      {/* ─── Log Panel ─── */}
      {showLogPanel && (
        <div
          style={{
            background: 'rgba(15, 23, 42, 0.6)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BellRing size={18} style={{ color: '#3b82f6' }} />
              <h3 style={{ color: '#f8fafc', fontSize: '15px', fontWeight: 700, margin: 0 }}>
                سجل إشعارات الخلفية (مراقبة الفوارق الزمنية)
              </h3>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['userLogs'] });
                  refetchUserLogs();
                }}
                disabled={isLogsFetching}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  color: isLogsFetching ? '#64748b' : '#94a3b8',
                  cursor: isLogsFetching ? 'not-allowed' : 'pointer',
                  opacity: isLogsFetching ? 0.7 : 1,
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <RefreshCw size={13} style={isLogsFetching ? { animation: 'spin 1s linear infinite' } : {}} />
                {isLogsFetching ? 'جاري التحديث...' : 'تحديث'}
              </button>
              {userLogs && userLogs.length > 0 && (
                <button
                  onClick={async () => {
                    if (window.confirm('هل أنت متأكد من مسح وإعادة ضبط سجل التنبيهات بالكامل؟')) {
                      try {
                        await notificationService.markRead({ all: true, delete: true, type: 'TICKET' });
                        setNewTicketCount(0);
                        knownTicketIdsRef.current.clear();
                        if (ticketsData && ticketsData.length > 0) {
                          ticketsData.forEach((t) => knownTicketIdsRef.current.add(t.id));
                        }
                        queryClient.setQueryData(['userLogs', currentSessionKey], []);
                        await refetchUserLogs();
                      } catch (err) {
                        console.error('Failed to clear logs:', err);
                      }
                    }
                  }}

                  style={{
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    color: '#ef4444',
                    cursor: 'pointer',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: 600,
                  }}
                >
                  <Trash2 size={13} />
                  مسح السجل
                </button>
              )}
            </div>
          </div>

          {isLogsLoading ? (
            <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px' }}>جاري تحميل السجلات...</div>
          ) : isLogsError ? (
            <div style={{ color: '#ef4444', fontSize: '13px', textAlign: 'center', padding: '20px' }}>
              تعذر تحميل السجلات من الخادم. يرجى إعادة المحاولة.
            </div>
          ) : !userLogs || userLogs.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '20px' }}>لا توجد إشعارات مسجلة بعد.</div>
          ) : (
            <div>
              {/* Responsive Layout: Cards for Mobile, Table for Desktop */}
              {isMobile ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {paginatedUserLogs.map((log: any) => {
                    const ticketCreated = log.ticketCreatedAt || log.metadata?.ticketCreatedAt || null;
                    const ticketDetected = log.ticketDetectedAt || log.metadata?.ticketDetectedAt || null;
                    const acceptedAt = log.oneSignalAcceptedAt || log.metadata?.oneSignalAcceptedAt || null;
                    const isHeld = Boolean(log.metadata?.isSafetyHold || log.metadata?.isHeld);

                    const diffCreatedDetected = getTimeDiff(ticketCreated, ticketDetected);
                    const diffDetectedAccepted = isHeld ? 'معلق (SAFETY_HOLD)' : (acceptedAt ? getTimeDiff(ticketDetected, acceptedAt) : '—');
                    return (
                      <div
                        key={log.id}
                        style={{
                          background: 'rgba(30, 41, 59, 0.7)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '10px',
                          padding: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          fontSize: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, color: '#f1f5f9', fontSize: '13px' }}>{log.title}</span>
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 600,
                              background: log.success ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                              color: log.success ? '#10b981' : '#ef4444',
                            }}
                          >
                            {log.success ? 'نجح' : 'فشل'}
                          </span>
                        </div>
                        {log.body && <div style={{ color: '#94a3b8', fontSize: '11px' }}>{log.body}</div>}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '4px', background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '6px' }}>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '10px', display: 'block' }}>إنشاء البوابة:</span>
                            <span style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '10px' }}>
                              {ticketCreated ? new Date(ticketCreated).toLocaleTimeString('ar-IQ') : '—'}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '10px', display: 'block' }}>اكتشاف السيستم:</span>
                            <span style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '10px' }}>
                              {ticketDetected ? new Date(ticketDetected).toLocaleTimeString('ar-IQ') : '—'}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '10px', display: 'block' }}>قبول OneSignal:</span>
                            <span style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '10px' }}>
                              {isHeld ? 'معلق (SAFETY_HOLD)' : (acceptedAt ? new Date(acceptedAt).toLocaleTimeString('ar-IQ') : '—')}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#64748b', fontSize: '10px', display: 'block' }}>فارق الاكتشاف:</span>
                            <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '10px' }}>{diffCreatedDetected}</span>
                          </div>
                          <div style={{ gridColumn: 'span 2' }}>
                            <span style={{ color: '#64748b', fontSize: '10px', display: 'block' }}>فارق قبول المزود:</span>
                            <span style={{ color: isHeld ? '#f59e0b' : '#10b981', fontWeight: 600, fontSize: '10px' }}>{diffDetectedAccepted}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', color: '#cbd5e1' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', textAlign: 'right' }}>
                        <th style={{ padding: '10px 8px' }}>العنوان</th>
                        <th style={{ padding: '10px 8px' }}>التفاصيل</th>
                        <th style={{ padding: '10px 8px' }}>إنشاء البوابة (National)</th>
                        <th style={{ padding: '10px 8px' }}>اكتشاف السيستم (Cron)</th>
                        <th style={{ padding: '10px 8px' }}>قبول إرسال OneSignal (API Accepted)</th>
                        <th style={{ padding: '10px 8px' }}>فارق الاكتشاف</th>
                        <th style={{ padding: '10px 8px' }}>فارق قبول المزود</th>
                        <th style={{ padding: '10px 8px' }}>الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUserLogs.map((log: any) => {
                        const ticketCreated = log.ticketCreatedAt || log.metadata?.ticketCreatedAt || null;
                        const ticketDetected = log.ticketDetectedAt || log.metadata?.ticketDetectedAt || null;
                        const acceptedAt = log.oneSignalAcceptedAt || log.metadata?.oneSignalAcceptedAt || null;
                        const isHeld = Boolean(log.metadata?.isSafetyHold || log.metadata?.isHeld);

                        const diffCreatedDetected = getTimeDiff(ticketCreated, ticketDetected);
                        const diffDetectedAccepted = isHeld ? 'معلق (SAFETY_HOLD)' : (acceptedAt ? getTimeDiff(ticketDetected, acceptedAt) : '—');
                        return (
                          <tr key={log.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                            <td style={{ padding: '10px 8px', fontWeight: 600, color: '#f1f5f9' }}>{log.title}</td>
                            <td style={{ padding: '10px 8px', color: '#94a3b8' }}>{log.body}</td>
                            <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>
                              {ticketCreated ? new Date(ticketCreated).toLocaleString('ar-IQ') : '—'}
                            </td>
                            <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>
                              {ticketDetected ? new Date(ticketDetected).toLocaleString('ar-IQ') : '—'}
                            </td>
                            <td style={{ padding: '10px 8px', fontFamily: 'monospace' }}>
                              {isHeld ? (
                                <span style={{ color: '#f59e0b', fontWeight: 600 }}>معلق (SAFETY_HOLD)</span>
                              ) : acceptedAt ? (
                                new Date(acceptedAt).toLocaleString('ar-IQ')
                              ) : (
                                '—'
                              )}
                            </td>
                            <td style={{ padding: '10px 8px', color: '#f59e0b' }}>{diffCreatedDetected}</td>
                            <td style={{ padding: '10px 8px', color: isHeld ? '#f59e0b' : '#10b981' }}>{diffDetectedAccepted}</td>
                            <td style={{ padding: '10px 8px' }}>
                              <span
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  background: log.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                                  color: log.success ? '#10b981' : '#ef4444',
                                }}
                              >
                                {log.success ? 'نجح' : 'فشل'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}


              {/* Pagination controls for Logs */}
              {totalLogsPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                    {`عرض ${(logsPageNumber - 1) * logsPageSize + 1}-${Math.min(logsPageNumber * logsPageSize, totalLogsCount)} من أصل ${totalLogsCount} تنبيه`}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setLogsPageNumber(prev => Math.max(prev - 1, 1))}
                      disabled={logsPageNumber === 1}
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        color: logsPageNumber === 1 ? '#475569' : '#94a3b8',
                        cursor: logsPageNumber === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      السابق
                    </button>
                    <span style={{ color: '#f8fafc', fontSize: '12px', display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                      {`صفحة ${logsPageNumber} من ${totalLogsPages}`}
                    </span>
                    <button
                      onClick={() => setLogsPageNumber(prev => Math.min(prev + 1, totalLogsPages))}
                      disabled={logsPageNumber >= totalLogsPages}
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        color: logsPageNumber >= totalLogsPages ? '#475569' : '#94a3b8',
                        cursor: logsPageNumber >= totalLogsPages ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      التالي
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Summary Error Notification Banner ─── */}
      {summaryState === 'error' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            marginBottom: '16px',
            borderRadius: '10px',
            background: isLight ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.15)',
            border: isLight ? '1px solid #fca5a5' : '1px solid rgba(239, 68, 68, 0.3)',
            color: isLight ? '#dc2626' : '#fca5a5',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          <span>⚠️ تعذر تحميل الإحصائيات العامة للتذاكر من الخادم</span>
          <button
            onClick={() => refetchSummary()}
            style={{
              background: '#dc2626',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '4px 12px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            إعادة المحاولة
          </button>
        </div>
      )}

      {/* ─── Stats Cards ─── */}
      <div
        className="tickets-stats-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {[
          { key: 'ALL', label: 'إجمالي التذاكر', val: summary.total, color: '#64748b', colorLight: '#475569', bgLight: '#f8fafc', borderLight: '#cbd5e1' },
          { key: 'OPEN', label: 'مفتوحة', val: summary.open, color: '#10b981', colorLight: '#059669', bgLight: '#ecfdf5', borderLight: '#a7f3d0' },
          { key: 'IN_PROGRESS', label: 'قيد المعالجة', val: summary.inProgress, color: '#3b82f6', colorLight: '#2563eb', bgLight: '#eff6ff', borderLight: '#bfdbfe' },
          { key: 'RESOLVED', label: 'تم الحل', val: summary.resolved, color: '#10b981', colorLight: '#059669', bgLight: '#f0fdf4', borderLight: '#bbf7d0' },
          { key: 'CLOSED', label: 'مغلقة', val: summary.closed, color: '#f87171', colorLight: '#dc2626', bgLight: '#fef2f2', borderLight: '#fecaca' },
        ].map((card) => {
          const isActive = statusFilter === card.key;
          const displayVal = summaryState === 'loading' && card.val === null ? '...' : (summaryState === 'error' && card.val === null ? '—' : (card.val ?? 0));
          return (
            <div
              key={card.key}
              onClick={() => {
                setStatusFilter(card.key);
                setPageNumber(1);
              }}
              style={{
                background: isActive 
                  ? (isLight ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.25)') 
                  : (isLight ? card.bgLight : 'rgba(13,31,60,0.6)'),
                border: isActive
                  ? '2px solid #3b82f6'
                  : (isLight ? `1.5px solid ${card.borderLight}` : '1px solid rgba(255,255,255,0.08)'),
                borderRadius: '12px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'right',
                boxShadow: isLight ? '0 4px 12px rgba(0, 0, 0, 0.03)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                transform: isActive ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              <span style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '12px', marginBottom: '8px', fontWeight: 700 }}>{card.label}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: isLight ? card.colorLight : card.color,
                    boxShadow: isActive ? `0 0 8px ${card.color}` : 'none',
                  }}
                />
                <span style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '24px', fontWeight: 800 }}>
                  {displayVal}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Filter Toolbar ─── */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.5)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: isLight ? '0 4px 12px rgba(0, 0, 0, 0.02)' : 'none',
        }}
      >
        <div style={{ position: 'relative', width: '280px', minWidth: '200px' }}>
          <Search
            size={16}
            color="#64748b"
            style={{ position: 'absolute', top: '50%', right: '12px', transform: 'translateY(-50%)' }}
          />
          <input
            type="text"
            placeholder="بحث برقم التذكرة، الموضوع، العميل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              background: isLight ? '#f8fafc' : 'rgba(10,22,40,0.8)',
              border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px',
              padding: '8px 36px 8px 12px',
              color: isLight ? '#1e293b' : '#e2e8f0',
              fontSize: '13px',
              outline: 'none',
              direction: 'rtl',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '12px', fontWeight: 600 }}>الحالة:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageNumber(1);
              }}
              style={{
                background: isLight ? '#f8fafc' : 'rgba(10,22,40,0.8)',
                border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                padding: '6px 12px',
                color: isLight ? '#1e293b' : '#e2e8f0',
                fontSize: '12.5px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="ALL">عرض الكل (جميع التذاكر)</option>
              <option value="OPEN">مفتوحة (جديدة / معاد فتحها / بانتظار الرد)</option>
              <option value="IN_PROGRESS">قيد المعالجة (قيد المعالجة / ملاحظات / معلقة)</option>
              <option value="RESOLVED">تم الحل (محلولة)</option>
              <option value="CLOSED">مغلقة (مغلقة / ملغاة)</option>
              <optgroup label="حالات تفصيلية">
                <option value="New">New (جديدة)</option>
                <option value="In progress">In progress (قيد المعالجة)</option>
                <option value="Contractor feedback">Contractor feedback (ملاحظات المقاول)</option>
                <option value="Waiting for respons">Waiting for respons (بانتظار الرد)</option>
                <option value="Reopened">Reopened (أعيد فتحها)</option>
                <option value="On hold">On hold (معلقة)</option>
                <option value="Resolved">Resolved (محلولة)</option>
                <option value="Closed">Closed (مغلقة)</option>
                <option value="Canceled">Canceled (ملغاة)</option>
              </optgroup>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '12px', fontWeight: 600 }}>الأولوية:</span>
            <select
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPageNumber(1);
              }}
              style={{
                background: isLight ? '#f8fafc' : 'rgba(10,22,40,0.8)',
                border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '8px',
                padding: '6px 12px',
                color: isLight ? '#1e293b' : '#e2e8f0',
                fontSize: '12.5px',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="ALL">الكل</option>
              <option value="LOW">منخفضة</option>
              <option value="NORMAL">عادية</option>
              <option value="HIGH">عالية</option>
              <option value="CRITICAL">حرجة</option>
            </select>
          </div>
        </div>
      </div>

      {/* ─── Tickets Table ─── */}
      <div
        style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          overflowX: 'auto',
          boxShadow: isLight ? '0 4px 12px rgba(0, 0, 0, 0.02)' : 'none',
        }}
      >
        <table className="responsive-table ticket-table" style={{ width: '100%', borderCollapse: 'collapse', direction: 'rtl' }}>
          <thead>
            <tr style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.04)' }}>
              {['رقم التذكرة', 'المشترك', 'الموضوع / التصنيف', 'الأولوية', 'الحالة', 'آخر تحديث', 'الإجراء'].map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    color: isLight ? '#475569' : '#64748b',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.08)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isListLoading && displayTickets.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: isLight ? '#475569' : '#64748b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <RefreshCw size={16} style={{ animation: 'spin 1.2s linear infinite' }} />
                    جاري جلب قائمة التذاكر من البوابة الوطنية...
                  </div>
                </td>
              </tr>
            ) : filteredTickets.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: isLight ? '#475569' : '#64748b' }}>
                  لا توجد تذاكر دعم فني تطابق فلاتر البحث
                </td>
              </tr>
            ) : (
              paginatedTickets.map((ticket: any) => {
                const priorityInfo = priorityLabels[ticket.priority] || { label: ticket.priority, color: '#fff', bg: 'transparent' };
                const statusInfo = statusLabels[ticket.status] || { label: ticket.status, color: '#fff', bg: 'transparent' };
                const updateDate = ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleDateString('ar-IQ') : '—';
                const isNew = !knownTicketIdsRef.current.has(ticket.id) ? false : newTicketCount > 0;

                return (
                  <tr
                    key={ticket.id}
                    className="ticket-card-row"
                    style={{
                      borderBottom: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.04)',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td
                      className="td-id"
                      data-label="رقم التذكرة"
                      style={{
                        padding: '14px 16px',
                        color: isLight ? '#475569' : '#94a3b8',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        direction: 'ltr',
                        textAlign: 'right',
                        fontWeight: 600,
                      }}
                    >
                      {ticket.displayId || ticket.id}
                    </td>

                    <td className="td-customer" data-label="المشترك" style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '13px', fontWeight: 600 }}>
                          {ticket.customerName || '—'}
                        </span>
                        {ticket.customerId && (
                          <span style={{ color: isLight ? '#64748b' : '#64748b', fontSize: '11px', direction: 'ltr' }}>
                            {ticket.customerId}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="td-subject" data-label="الموضوع / التصنيف" style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '350px' }}>
                        <span
                          style={{
                            color: isLight ? '#0f172a' : '#e2e8f0',
                            fontSize: '13px',
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            textAlign: 'right',
                          }}
                        >
                          {ticket.subject}
                        </span>
                        {ticket.summary && (
                          <span
                            style={{
                              color: isLight ? '#475569' : '#94a3b8',
                              fontSize: '11px',
                              marginTop: '4px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              textAlign: 'right',
                            }}
                            title={ticket.summary}
                          >
                            {ticket.summary}
                          </span>
                        )}
                        <span
                          style={{
                            color: isLight ? '#2563eb' : '#3b82f6',
                            fontSize: '10px',
                            background: isLight ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.08)',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            marginTop: '4px',
                            display: 'inline-block',
                          }}
                        >
                          {ticket.category}
                        </span>
                      </div>
                    </td>

                    <td className="td-priority" data-label="الأولوية" style={{ padding: '14px 16px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          color: priorityInfo.color,
                          backgroundColor: priorityInfo.bg,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                        }}
                      >
                        {priorityInfo.label}
                      </span>
                    </td>

                    <td className="td-status" data-label="الحالة" style={{ padding: '14px 16px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          color: statusInfo.color,
                          backgroundColor: statusInfo.bg,
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                        }}
                      >
                        {statusInfo.label}
                      </span>
                    </td>

                    <td
                      className="td-update"
                      data-label="آخر تحديث"
                      style={{
                        padding: '14px 16px',
                        color: isLight ? '#475569' : '#64748b',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        direction: 'ltr',
                        textAlign: 'right',
                      }}
                    >
                      {updateDate}
                    </td>

                    <td className="td-action" data-label="الإجراء" style={{ padding: '14px 16px' }}>
                      <button
                        onClick={() => setSelectedTicketId(ticket.id)}
                        style={{
                          background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: '6px',
                          padding: '4px 10px',
                          color: isLight ? '#334155' : '#94a3b8',
                          fontSize: '12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isLight ? '#e2e8f0' : 'rgba(59,130,246,0.1)';
                          e.currentTarget.style.borderColor = isLight ? '#3b82f6' : 'rgba(59,130,246,0.3)';
                          e.currentTarget.style.color = isLight ? '#2563eb' : '#60a5fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)';
                          e.currentTarget.style.borderColor = isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.color = isLight ? '#334155' : '#94a3b8';
                        }}
                      >
                        فتح
                        <ChevronRight size={12} style={{ transform: 'rotate(180deg)' }} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination Controls ─── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '16px',
          padding: '12px 16px',
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.5)',
          border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.06)',
          borderRadius: '12px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* Page size selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13px' }}>عرض:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPageNumber(1);
            }}
            style={{
              background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: '6px',
              padding: '4px 8px',
              color: isLight ? '#1e293b' : '#e2e8f0',
              fontSize: '13px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size} style={{ background: isLight ? '#ffffff' : '#0d1f3c', color: isLight ? '#1e293b' : '#e2e8f0' }}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
            disabled={pageNumber === 1 || isListLoading}
            className="pagination-btn pagination-prev"
          >
            <ChevronRight size={16} style={{ marginLeft: '4px' }} />
            السابق
          </button>
          
          <span style={{ color: isLight ? '#475569' : '#e2e8f0', fontSize: '13.5px', fontWeight: 600 }}>
            الصفحة {pageNumber} من {totalPages}
          </span>

          <button
            onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages))}
            disabled={pageNumber >= totalPages || isListLoading}
            className="pagination-btn pagination-next"
          >
            التالي
            <ChevronLeft size={16} style={{ marginRight: '4px' }} />
          </button>
        </div>

        {/* Total stats */}
        <div style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13px' }}>
          يظهر {totalFilteredCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1}-{Math.min(pageNumber * pageSize, totalFilteredCount)} من أصل {totalFilteredCount} تذكرة
        </div>
      </div>

      {/* ─── Ticket Details Drawer ─── */}
      {selectedTicketId && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setSelectedTicketId(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              zIndex: 999,
            }}
          />

          {/* Drawer Panel */}
          <div
            style={{
              position: 'fixed',
              top: isMobile ? '56px' : 0,
              left: 0,
              bottom: 0,
              width: isMobile ? '100vw' : '550px',
              maxWidth: '100vw',
              background: isLight ? '#ffffff' : 'linear-gradient(185deg, #09172c 0%, #060e1b 100%)',
              borderRight: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: isLight ? '-10px 0px 30px rgba(0, 0, 0, 0.05)' : '20px 0px 50px rgba(0, 0, 0, 0.8)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              direction: 'rtl',
              animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '20px',
                borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <button
                onClick={() => setSelectedTicketId(null)}
                style={{
                  background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)'}`,
                  borderRadius: '8px',
                  padding: '8px',
                  color: isLight ? '#475569' : '#94a3b8',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.06)';
                  e.currentTarget.style.color = isLight ? '#475569' : '#94a3b8';
                }}
              >
                <X size={18} />
              </button>

              <div style={{ textAlign: 'right', flex: 1, marginRight: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {activeTicket && (
                    <span
                      title={`حالة التذكرة: ${activeTicket.rawStatus || activeTicket.status}`}
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: 
                          activeTicket.closedAt || activeTicket.status?.toLowerCase() === 'closed' || activeTicket.rawStatus?.toUpperCase() === 'CLOSED'
                            ? '#ef4444' 
                            : (activeTicket.status?.toLowerCase() === 'in_progress' ? '#3b82f6' : '#10b981'),
                        boxShadow: 
                          activeTicket.closedAt || activeTicket.status?.toLowerCase() === 'closed' || activeTicket.rawStatus?.toUpperCase() === 'CLOSED'
                            ? '0 0 10px #ef4444, 0 0 20px rgba(239, 68, 68, 0.6)' 
                            : (activeTicket.status?.toLowerCase() === 'in_progress' ? '0 0 10px #3b82f6, 0 0 20px rgba(59, 130, 246, 0.6)' : '0 0 10px #10b981, 0 0 20px rgba(16, 185, 129, 0.6)'),
                        display: 'inline-block',
                        animation: 'pulse 2s infinite ease-in-out',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <h3 style={{ color: isLight ? '#0f172a' : '#f8fafc', fontSize: '18px', fontWeight: 800, margin: 0, letterSpacing: '-0.2px' }}>
                    {isDetailsLoading && !activeTicket ? 'جاري التحميل...' : activeTicket?.subject}
                  </h3>
                </div>
              </div>
            </div>

            {/* Info Grid Cards & Full Portal Data */}
            {activeTicket && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                {/* 1. Top Quick Cards: Zone & Problem Type matching Image 2 */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
                  padding: '12px 16px', background: isLight ? '#f1f5f9' : 'rgba(15, 23, 42, 0.6)',
                  borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.08)'
                }}>
                  <div style={{
                    background: isLight ? '#ffffff' : 'rgba(30, 41, 59, 0.7)',
                    padding: '10px 14px', borderRadius: '10px',
                    border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
                  }}>
                    <span style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '11px', fontWeight: 600 }}>المنطقة</span>
                    <span style={{ color: '#0d6efd', fontSize: '14px', fontWeight: 800, marginTop: '2px', fontFamily: 'monospace' }}>
                      {activeTicket.zone}
                    </span>
                  </div>

                  <div style={{
                    background: isLight ? '#ffffff' : 'rgba(30, 41, 59, 0.7)',
                    padding: '10px 14px', borderRadius: '10px',
                    border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
                  }}>
                    <span style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '11px', fontWeight: 600 }}>نوع المشكله</span>
                    <span style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '13px', fontWeight: 700, marginTop: '2px' }}>
                      {activeTicket.category}
                    </span>
                  </div>
                </div>

                {/* 2. Description Section (Positioned First on Mobile View as requested) */}
                {activeTicket?.summary && (
                  <div
                    style={{
                      padding: '16px 20px',
                      background: isLight ? 'rgba(59, 130, 246, 0.05)' : 'rgba(59, 130, 246, 0.05)',
                      borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.06)',
                    }}
                  >
                    <span style={{ color: isLight ? '#2563eb' : '#3b82f6', fontSize: '11.5px', display: 'block', marginBottom: '4px', fontWeight: 700 }}>
                      وصف المشكلة / التفاصيل
                    </span>
                    <p style={{ color: isLight ? '#1e293b' : '#e2e8f0', fontSize: '13.5px', margin: 0, lineHeight: '1.5', fontWeight: 500 }}>
                      {activeTicket.summary}
                    </p>
                  </div>
                )}

                {/* 3. Comprehensive Portal Details Grid (Positioned Below Description) */}
                <div
                  style={{
                    padding: '16px 20px',
                    background: isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.02)',
                    borderBottom: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.06)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '14px',
                  }}
                >
                  <div>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>معرف التذكره</span>
                    <span style={{ color: isLight ? '#2563eb' : '#60a5fa', fontSize: '13px', fontFamily: 'monospace', fontWeight: 700 }}>
                      {activeTicket.displayId || selectedTicketId}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block', marginBottom: '2px' }}>الحاله</span>
                    <span
                      style={{
                        fontSize: '11px',
                        color: statusLabels[activeTicket.status]?.color || '#f87171',
                        background: statusLabels[activeTicket.status]?.bg || 'rgba(239,68,68,0.1)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        display: 'inline-block',
                      }}
                    >
                      {activeTicket.rawStatus || (activeTicket.status === 'closed' ? 'CLOSED' : activeTicket.status)}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>تاريخ الانشاء</span>
                    <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '12.5px', direction: 'ltr', textAlign: 'right', fontWeight: 600 }}>
                      {formatPortalDate(activeTicket.createdAt)}
                    </span>
                  </div>

                  <div>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>تاريخ التحديث</span>
                    <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '12.5px', direction: 'ltr', textAlign: 'right', fontWeight: 600 }}>
                      {formatPortalDate(activeTicket.updatedAt || activeTicket.createdAt)}
                    </span>
                  </div>

                  {activeTicket.closedAt && (
                    <div>
                      <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>تاريخ الاغلاق</span>
                      <span style={{ color: isLight ? '#334155' : '#94a3b8', fontSize: '12.5px', direction: 'ltr', textAlign: 'right', fontWeight: 600 }}>
                        {formatPortalDate(activeTicket.closedAt)}
                      </span>
                    </div>
                  )}

                  <div>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>رقم هاتف المتصل</span>
                    <span style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '13px', fontFamily: 'monospace', direction: 'ltr', textAlign: 'right', fontWeight: 700 }}>
                      {activeTicket.callerPhone}
                    </span>
                  </div>

                  <div style={{ gridColumn: 'span 2' }}>
                    <span style={{ color: isLight ? '#475569' : '#64748b', fontSize: '11px', display: 'block' }}>المنشئ</span>
                    <span style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '13px', fontWeight: 700 }}>
                      {activeTicket.creatorName !== '—' ? activeTicket.creatorName : activeTicket.customerName}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Comments */}
            <div
              style={{
                flex: 1,
                padding: '20px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                background: isLight ? '#f1f5f9' : 'rgba(5, 10, 20, 0.3)',
              }}
            >
              {isCommentsLoading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    color: isLight ? '#475569' : '#64748b',
                    gap: '12px',
                  }}
                >
                  <RefreshCw size={24} style={{ animation: 'spin 1.2s linear infinite' }} />
                  <span>جاري تحميل الرسائل والمناقشات...</span>
                </div>
              ) : !Array.isArray(ticketComments) || ticketComments.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    color: isLight ? '#475569' : '#64748b',
                    textAlign: 'center',
                    padding: '20px',
                  }}
                >
                  <HelpCircle size={32} style={{ marginBottom: '8px', color: isLight ? '#cbd5e1' : '#475569' }} />
                  <span style={{ fontSize: '13.5px', fontWeight: 600 }}>لا توجد تعليقات على التذكرة حتى الآن.</span>
                </div>
              ) : (
                ticketComments.map((comment: any) => {
                  const isSystemMsg = comment.isSystem;
                  return (
                    <div
                      key={comment.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isSystemMsg ? 'flex-start' : 'flex-end',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '4px',
                          fontSize: '11px',
                          color: isLight ? '#64748b' : '#64748b',
                        }}
                      >
                        <span style={{ fontWeight: 600, color: isSystemMsg ? (isLight ? '#2563eb' : '#60a5fa') : (isLight ? '#475569' : '#94a3b8') }}>
                          {comment.creatorName}
                        </span>
                        <span>•</span>
                        <span>
                          {comment.createdAt
                            ? new Date(comment.createdAt).toLocaleTimeString('ar-IQ', { hour: '2-digit', minute: '2-digit' })
                            : ''}
                        </span>
                      </div>
                      <div
                        style={{
                          background: isSystemMsg 
                            ? (isLight ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.15)') 
                            : (isLight ? '#ffffff' : 'rgba(255, 255, 255, 0.05)'),
                          border: isSystemMsg 
                            ? (isLight ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(59, 130, 246, 0.25)') 
                            : (isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.08)'),
                          color: isLight ? '#0f172a' : '#e2e8f0',
                          padding: '12px 16px',
                          borderRadius: isSystemMsg ? '0px 16px 16px 16px' : '16px 0px 16px 16px',
                          maxWidth: '85%',
                          fontSize: '13px',
                          lineHeight: '1.5',
                          whiteSpace: 'pre-wrap',
                          boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.04)' : '0 4px 12px rgba(0,0,0,0.15)',
                        }}
                      >
                        {comment.content}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Comment Form matching Portal Image 1 */}
            {(() => {
              const statusMapped = activeTicket?.status ? mapStatusToMain(activeTicket.status) : '';
              const rawMapped = activeTicket?.rawStatus ? mapStatusToMain(activeTicket.rawStatus) : '';
              const isClosedTicket = 
                Boolean(activeTicket?.closedAt) ||
                statusMapped === 'CLOSED' || 
                rawMapped === 'CLOSED' ||
                activeTicket?.status?.toLowerCase() === 'closed' || 
                activeTicket?.status?.toLowerCase() === 'close' ||
                activeTicket?.rawStatus?.toLowerCase() === 'closed' ||
                activeTicket?.rawStatus?.toLowerCase() === 'close';

              return (
                <div
                  style={{
                    padding: isMobile ? '16px 16px calc(16px + env(safe-area-inset-bottom)) 16px' : '16px 20px',
                    borderTop: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255, 255, 255, 0.08)',
                    background: isLight ? '#ffffff' : 'rgba(9, 23, 44, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}
                >
                  <form
                    onSubmit={handleSubmitComment}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      alignItems: 'stretch',
                    }}
                  >
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder={!permissions['tickets.reply'] ? "🔒 غير مصرح لك بكتابة تعليق (يتطلب صلاحية tickets.reply)..." : (isClosedTicket ? "اكتب تعليقك هنا ..." : "اكتب تعليقك هنا ...")}
                      rows={3}
                      disabled={isClosedTicket || isSubmittingComment || !permissions['tickets.reply']}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        background: isLight ? '#f8fafc' : 'rgba(5, 12, 24, 0.8)',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                        borderRadius: '8px',
                        padding: '10px 12px',
                        color: isLight ? '#1e293b' : '#e2e8f0',
                        fontSize: '13px',
                        outline: 'none',
                        resize: 'none',
                        fontFamily: 'inherit',
                        lineHeight: '1.4',
                        opacity: (isClosedTicket || !permissions['tickets.reply']) ? 0.7 : 1,
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !isClosedTicket && permissions['tickets.reply']) {
                          e.preventDefault();
                          handleSubmitComment(e);
                        }
                      }}
                    />

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                      <button
                        type="submit"
                        disabled={isClosedTicket || isSubmittingComment || !newComment.trim() || !permissions['tickets.reply']}
                        title={!permissions['tickets.reply'] ? '🔒 غير مصرح (يتطلب صلاحية tickets.reply)' : ''}
                        style={{
                          background: isClosedTicket
                            ? 'rgba(248, 113, 113, 0.25)'
                            : (!permissions['tickets.reply']
                              ? 'rgba(100, 116, 139, 0.2)'
                              : (!newComment.trim()
                                ? (isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.04)')
                                : 'linear-gradient(90deg, #3b82f6, #6366f1)')),
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 28px',
                          color: isClosedTicket
                            ? '#fca5a5'
                            : (!permissions['tickets.reply']
                              ? '#94a3b8'
                              : (!newComment.trim() ? (isLight ? '#94a3b8' : '#475569') : '#fff')),
                          cursor: (isClosedTicket || !newComment.trim() || !permissions['tickets.reply']) ? 'not-allowed' : 'pointer',
                          fontWeight: 700,
                          fontSize: '13.5px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                        }}
                      >
                        {isSubmittingComment ? (
                          <RefreshCw size={16} style={{ animation: 'spin 1.2s linear infinite' }} />
                        ) : (!permissions['tickets.reply'] ? '🔒 اضافة تعليق (مقيد)' : 'اضافة تعليق')}
                      </button>
                    </div>
                  </form>

                  {/* Permission warning for comments */}
                  {!permissions['tickets.reply'] && !isClosedTicket && (
                    <div
                      style={{
                        color: '#f87171',
                        fontSize: '12px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginTop: '4px',
                      }}
                    >
                      🔒 تنبيه: حسابك لا يمتلك صلاحية إضافة تعليق/رد على التذاكر (<code>tickets.reply</code>)
                    </div>
                  )}

                  {/* Portal Closed Ticket Notice matching Portal Image 1 */}
                  {isClosedTicket && (
                    <div
                      style={{
                        color: isLight ? '#991b1b' : '#fca5a5',
                        fontSize: '12.5px',
                        fontWeight: 600,
                        textAlign: 'center',
                        marginTop: '4px',
                        marginBottom: '2px',
                        lineHeight: '1.4',
                      }}
                    >
                      ملاحظة: لا يمكنك إضافة تعليق على هذه التذكرة لأنها تذكرة مغلقة
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

export default TicketsPage;
